import React, { useCallback, useEffect, useState } from "react";
import { Check, X, FileText, ExternalLink } from "lucide-react";
import { apiGet, apiGetBlob, apiPost } from "../../Api/Client.js";
import { errorText } from "../../Api/errorText.js";
import { approvalConfirmed } from "./joiningReviewConfirm.js";

// Identity documents waiting on a decision. Approving one is what actually
// creates the candidate's account and sends their credentials — nothing before
// this point has created a login — so approving asks the reviewer to confirm
// first, and rejecting asks for a reason the candidate will read.

export function JoiningReviewScreen() {
  const [rows, setRows] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [note, setNote] = useState({});
  const [result, setResult] = useState(null);

  // A failed load is shown as a failure, never as an empty "Nothing waiting" list.
  const load = useCallback(() => {
    setLoadError("");
    apiGet("/MainApp/joining-reviews/")
      .then((r) => setRows(r?.results || []))
      .catch((e) => setLoadError(errorText(e, "The list could not be loaded.")));
  }, []);

  useEffect(() => { load(); }, [load]);

  // The file is fetched with the reviewer's credentials and shown from memory.
  // It has no public URL: the server checks, every time, that this person may
  // see it, and records that they did.
  const openDocument = async (row) => {
    setResult(null);
    const preview = window.open("", "_blank");
    try {
      const blob = await apiGetBlob(row.file_url);
      const url = URL.createObjectURL(blob);
      if (preview) preview.location.href = url;
      else window.location.assign(url);
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      if (preview) preview.close();
      setResult({ ok: false, message: e?.message || "The document could not be opened." });
    }
  };

  const decide = async (id, decision) => {
    if (decision === "reject" && !(note[id] || "").trim()) {
      setResult({ ok: false, message: "Tell them what was wrong — they will read it." });
      return;
    }
    const row = (rows || []).find((x) => x.id === id);
    if (!approvalConfirmed(decision, row?.candidate_name, (q) => window.confirm(q))) return;
    setBusyId(id); setResult(null);
    try {
      const r = await apiPost(`/MainApp/joining-reviews/${id}/decide`, {
        decision, note: note[id] || "",
      });
      setResult({
        ok: true,
        message: decision === "approve"
          ? (r?.account_created ? "Approved. Their account is created and credentials are on the way."
                                : `Approved, but the account was not created: ${r?.detail || "unknown reason"}`)
          : "Sent back for a new copy.",
      });
      load();
    } catch (e) {
      setResult({ ok: false, message: e?.payload?.detail || e?.message || "That did not go through." });
    } finally { setBusyId(null); }
  };

  const card = { background: "var(--surface)", border: "1px solid var(--c-200)", borderRadius: 12, padding: 18 };

  return (
    <section style={{ padding: "24px 28px", maxWidth: 900, margin: "0 auto" }}>
      <div style={{ marginBottom: 20 }}>
        <span style={{ fontSize: 12, color: "var(--c-500)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>
          Hiring
        </span>
        <h1 style={{ margin: "4px 0 6px", fontSize: 26, fontWeight: 700 }}>IDs waiting on you</h1>
        <p style={{ margin: 0, fontSize: 14, color: "var(--c-500)", maxWidth: "62ch" }}>
          Each of these people has read the culture points, accepted confidentiality and accepted their
          offer. Approving creates their account and sends their login details.
        </p>
      </div>

      {result && (
        <div className={result.ok ? "Auth-AlertOk" : "Auth-Alert"} style={{ marginBottom: 16 }}>{result.message}</div>
      )}

      {loadError && (
        <div className="Auth-Alert" style={{ marginBottom: 16 }}>
          {loadError} <button type="button" className="Soft-Button Small" onClick={load}>Try again</button>
        </div>
      )}
      {rows === null && !loadError && <p style={{ color: "var(--c-500)" }}>Loading…</p>}
      {rows !== null && rows.length === 0 && !loadError && (
        <div style={{ ...card, textAlign: "center", color: "var(--c-500)" }}>
          Nothing waiting. Documents appear here the moment a candidate uploads one.
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {(rows || []).map((r) => (
          <div key={r.id} style={card}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "baseline", justifyContent: "space-between" }}>
              <div>
                <strong style={{ fontSize: 16 }}>{r.candidate_name}</strong>
                <div style={{ fontSize: 13, color: "var(--c-500)", marginTop: 2 }}>
                  {r.position_title}{r.department ? ` · ${r.department}` : ""}
                </div>
              </div>
              <button type="button" className="Soft-Button Small" onClick={() => openDocument(r)}
                      disabled={!r.file_url}>
                <FileText size={14} /> {r.file_name || "Open document"} <ExternalLink size={12} />
              </button>
            </div>

            <input
              className="Mini-Inp"
              style={{ marginTop: 14 }}
              placeholder="If sending it back, say what was wrong — the candidate reads this."
              value={note[r.id] || ""}
              onChange={(e) => setNote((n) => ({ ...n, [r.id]: e.target.value }))}
            />

            <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
              <button className="Primary-Button Small" disabled={busyId === r.id}
                      onClick={() => decide(r.id, "approve")}>
                <Check size={14} /> Approve and create their account
              </button>
              <button className="Soft-Button Small Danger" disabled={busyId === r.id}
                      onClick={() => decide(r.id, "reject")}>
                <X size={14} /> Ask for a new copy
              </button>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
